import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap, useMapEvents, WMSTileLayer, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toPng } from 'html-to-image';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { RotateCw, Move, ZoomIn, Camera, Layers } from 'lucide-react';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Update map view - SOLO se usa para centrar inicialmente, NO para actualizaciones
function ChangeView({ center, zoom, shouldUpdate }) {
  const map = useMap();
  useEffect(() => {
    // Solo actualizar si shouldUpdate es true (inicial o búsqueda externa)
    if (shouldUpdate && center && center[0] && center[1] && map && map._loaded) {
      try {
        map.setView(center, zoom);
      } catch (error) {
        console.error('Error updating map view:', error);
      }
    }
  }, [center, zoom, map, shouldUpdate]);
  return null;
}

// Handle map clicks
function MapClickHandler({ onClick, enabled }) {
  useMapEvents({
    click: (e) => {
      if (enabled) {
        onClick(e.latlng);
      }
    },
  });
  return null;
}

// Calculate pool polygon with rotation
function getPoolPolygon(lat, lon, length, width, rotation = 0) {
  const metersPerDegreeLat = 111320;
  const metersPerDegreeLon = 111320 * Math.cos((lat * Math.PI) / 180);
  
  // Half dimensions in degrees
  const halfLengthLat = (length / 2) / metersPerDegreeLat;
  const halfWidthLon = (width / 2) / metersPerDegreeLon;
  
  // Corner points (before rotation)
  const corners = [
    [-halfLengthLat, -halfWidthLon], // SW
    [-halfLengthLat, halfWidthLon],  // SE
    [halfLengthLat, halfWidthLon],   // NE
    [halfLengthLat, -halfWidthLon],  // NW
  ];
  
  // Rotate corners
  const rad = (rotation * Math.PI) / 180;
  const rotatedCorners = corners.map(([dLat, dLon]) => {
    const newDLat = dLat * Math.cos(rad) - dLon * Math.sin(rad);
    const newDLon = dLat * Math.sin(rad) + dLon * Math.cos(rad);
    return [lat + newDLat, lon + newDLon];
  });
  
  return rotatedCorners;
}

const PoolMapAdvanced = ({ 
  center = [40.4168, -3.7038],
  zoom = 18,
  onLocationSelect,
  onMapCapture,
  poolDimensions = null,
  initialRotation = 0,
  editable = true
}) => {
  const [mapCenter, setMapCenter] = useState(center);
  const [mapZoom, setMapZoom] = useState(zoom);
  const [poolRotation, setPoolRotation] = useState(initialRotation);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isDraggingPool, setIsDraggingPool] = useState(false);
  const [showCatastro, setShowCatastro] = useState(true);
  // Control para evitar re-centrar el mapa cuando el usuario interactúa
  const [shouldUpdateView, setShouldUpdateView] = useState(true);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    // Solo actualizar mapCenter desde props si es un cambio externo significativo
    // (ej: nueva búsqueda de dirección)
    if (center && center[0] && center[1]) {
      const latDiff = Math.abs(center[0] - mapCenter[0]);
      const lngDiff = Math.abs(center[1] - mapCenter[1]);
      // Solo centrar si el cambio es mayor a 0.001 grados (~100m)
      if (latDiff > 0.001 || lngDiff > 0.001) {
        setMapCenter(center);
        setShouldUpdateView(true);
      }
    }
  }, [center]);

  const handleMapClick = useCallback((latlng) => {
    if (editable && onLocationSelect) {
      const newCenter = [latlng.lat, latlng.lng];
      setMapCenter(newCenter);
      // NO actualizar la vista del mapa - el usuario ya está donde quiere
      setShouldUpdateView(false);
      onLocationSelect({
        latitude: latlng.lat,
        longitude: latlng.lng,
        rotation: poolRotation
      });
    }
  }, [editable, onLocationSelect, poolRotation]);

  const handleRotationChange = useCallback((value) => {
    const newRotation = value[0];
    setPoolRotation(newRotation);
    // NO actualizar la vista - solo cambia la rotación del polígono
    setShouldUpdateView(false);
    if (onLocationSelect && mapCenter[0] && mapCenter[1]) {
      onLocationSelect({
        latitude: mapCenter[0],
        longitude: mapCenter[1],
        rotation: newRotation
      });
    }
  }, [mapCenter, onLocationSelect]);

  // Capture map as image
  const captureMap = useCallback(async () => {
    if (!mapContainerRef.current) return null;
    
    try {
      const dataUrl = await toPng(mapContainerRef.current, {
        quality: 0.95,
        backgroundColor: '#fff',
        width: 800,
        height: 500,
      });
      
      if (onMapCapture) {
        onMapCapture(dataUrl);
      }
      
      return dataUrl;
    } catch (error) {
      console.error('Error capturing map:', error);
      return null;
    }
  }, [onMapCapture]);

  // Pool polygon
  const poolPolygon = poolDimensions && mapCenter[0] && mapCenter[1]
    ? getPoolPolygon(mapCenter[0], mapCenter[1], poolDimensions.length, poolDimensions.width, poolRotation)
    : null;

  return (
    <div className="space-y-4">
      {/* Map container */}
      <div 
        ref={mapContainerRef}
        style={{ 
          height: '450px', 
          width: '100%', 
          borderRadius: '12px', 
          overflow: 'hidden', 
          border: '2px solid #1E88E5',
          position: 'relative'
        }}
      >
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          whenReady={(map) => {
            mapRef.current = map.target;
            setIsMapReady(true);
          }}
        >
          {/* Base layer - OpenStreetMap */}
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Catastro WMS Layer - Parcelas */}
          {showCatastro && (
            <WMSTileLayer
              url="https://ovc.catastro.meh.es/Cartografia/WMS/ServidorWMS.aspx"
              layers="Catastro"
              format="image/png"
              transparent={true}
              opacity={0.7}
              attribution="&copy; Catastro"
            />
          )}
          
          {isMapReady && <ChangeView center={mapCenter} zoom={mapZoom} shouldUpdate={shouldUpdateView} />}
          
          {editable && <MapClickHandler onClick={handleMapClick} enabled={!isDraggingPool} />}
          
          {/* Pool marker */}
          {mapCenter[0] && mapCenter[1] && (
            <Marker 
              position={mapCenter}
              draggable={editable}
              eventHandlers={{
                dragstart: () => setIsDraggingPool(true),
                dragend: (e) => {
                  setIsDraggingPool(false);
                  const { lat, lng } = e.target.getLatLng();
                  setMapCenter([lat, lng]);
                  // NO re-centrar el mapa al arrastrar
                  setShouldUpdateView(false);
                  if (onLocationSelect) {
                    onLocationSelect({
                      latitude: lat,
                      longitude: lng,
                      rotation: poolRotation
                    });
                  }
                }
              }}
            >
              <Popup>
                <div className="text-sm">
                  <strong>Ubicación de la piscina</strong><br />
                  Lat: {mapCenter[0].toFixed(6)}<br />
                  Lon: {mapCenter[1].toFixed(6)}<br />
                  Rotación: {poolRotation}°
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* Pool shape */}
          {poolPolygon && (
            <Polygon
              positions={poolPolygon}
              pathOptions={{ 
                color: '#1E88E5', 
                weight: 3,
                fillColor: '#75CCF5',
                fillOpacity: 0.5,
                dashArray: '5, 5'
              }}
            >
              <Popup>
                <div className="text-sm">
                  <strong>Piscina</strong><br />
                  {poolDimensions.length} m × {poolDimensions.width} m<br />
                  Orientación: {poolRotation}°
                </div>
              </Popup>
            </Polygon>
          )}
        </MapContainer>

        {/* Map controls overlay */}
        <div className="absolute top-3 right-3 z-[1000] flex flex-col gap-2">
          <Button
            size="sm"
            variant={showCatastro ? "default" : "outline"}
            onClick={() => setShowCatastro(!showCatastro)}
            className="bg-white text-slate-700 border shadow-md hover:bg-slate-100"
            title="Mostrar/ocultar parcelas del Catastro"
          >
            <Layers className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={captureMap}
            className="bg-white text-slate-700 border shadow-md hover:bg-slate-100"
            title="Capturar imagen del mapa"
          >
            <Camera className="w-4 h-4" />
          </Button>
        </div>

        {/* Coordinates display */}
        <div className="absolute bottom-3 left-3 z-[1000] bg-white/90 px-3 py-2 rounded-lg shadow-md text-xs font-mono">
          {mapCenter[0]?.toFixed(6)}, {mapCenter[1]?.toFixed(6)}
        </div>
      </div>

      {/* Rotation control */}
      {editable && poolDimensions && (
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <RotateCw className="w-4 h-4" />
              Orientación de la piscina
            </div>
            <div className="flex-1">
              <Slider
                value={[poolRotation]}
                onValueChange={handleRotationChange}
                max={360}
                min={0}
                step={5}
                className="w-full"
              />
            </div>
            <div className="w-16 text-center font-mono text-sm bg-slate-100 rounded px-2 py-1">
              {poolRotation}°
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Haz clic en el mapa para posicionar la piscina. Arrastra el marcador para ajustar. Usa el control para rotar.
          </p>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        <div className="flex items-start gap-2">
          <Move className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Instrucciones:</strong>
            <ul className="mt-1 list-disc list-inside text-xs space-y-1">
              <li>Haz clic en el mapa para ubicar la piscina</li>
              <li>Arrastra el marcador azul para ajustar la posición</li>
              <li>Usa el control deslizante para orientar la piscina</li>
              <li>El botón <Layers className="w-3 h-3 inline" /> activa/desactiva las parcelas del Catastro</li>
              <li>El botón <Camera className="w-3 h-3 inline" /> captura la imagen para el PDF</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoolMapAdvanced;
